import { datadogRum } from '@datadog/browser-rum';
import {
  useMutation,
  useQuery,
  useInfiniteQuery,
  InfiniteData,
} from '@tanstack/react-query';
import { GraphQLClient } from 'graphql-request';

import { useAuth } from '@/components/Providers/Auth';
import { showAdobeAnalytics, trackAdobeErrorEvent } from '@/lib/adobe';
import { broadcastLogout } from '@/utils/broadcastLogout';
import { getInitialPreferences } from '@/utils/cookies';
import { API_ENDPOINT } from '@/utils/environment';
import { getErrorDescriptionWithType } from '@/utils/errors';
import { isKrakenErrorResponse } from '@/utils/request/types-response';

import type {
  ResponseWithEdges,
  UseKrakenInfiniteQueryArgs,
  UseKrakenMutationArgs,
  UseKrakenQueryArgs,
} from './types';

const client = new GraphQLClient(API_ENDPOINT);

export const handleKrakenErrors = ({
  errorResponse,
  onRefreshExpired,
  onAccessTokenExpired,
  onAuthHeaderNotProvided,
}: {
  errorResponse: unknown;
  onRefreshExpired: () => void;
  onAccessTokenExpired: () => void;
  onAuthHeaderNotProvided: () => void;
}): void => {
  if (!isKrakenErrorResponse(errorResponse)) {
    return;
  }

  const { initialAnalyticsState: hasAcceptedAnalyticsCookies } =
    getInitialPreferences();

  const [firstError] = errorResponse.response.errors;
  const { errorType } = getErrorDescriptionWithType(firstError);

  if (showAdobeAnalytics && hasAcceptedAnalyticsCookies) {
    trackAdobeErrorEvent(firstError.extensions);
  }

  if (errorType === 'UNAUTHORIZED_SESSION') {
    // Trigger logout across all tabs
    broadcastLogout('UNAUTHORIZED_SESSION');
    return;
  }

  if (errorType === 'REFRESH_TOKEN_EXPIRED') {
    onRefreshExpired();
    return;
  }

  if (errorType === 'ACCESS_TOKEN_EXPIRED') {
    onAccessTokenExpired();
    return;
  }

  if (errorType === 'AUTH_HEADER_NOT_PROVIDED') {
    onAuthHeaderNotProvided();
    return;
  }

  datadogRum.addError(new Error('GraphQL API error'), {
    errorCode: firstError.extensions?.errorCode,
    errorClass: firstError.extensions?.errorClass,
    errorType: firstError.extensions?.errorType,
    errorDescription: firstError.extensions?.errorDescription,
  });
};

export function useKrakenQuery<D, E, TSelectData = D>({
  query,
  queryKey,
  validateFn,
  requestHeaders,
  silentErrors,
  enabled = true,
  variables,
  select,
  ...options
}: UseKrakenQueryArgs<D, E, TSelectData>) {
  const {
    accessToken,
    masqueradeToken,
    hasLoaded,
    clearAccessTokens,
    expireSession,
  } = useAuth();

  /**
   * Token to use as Authorization value.
   */
  const authToken = masqueradeToken ?? accessToken;

  return useQuery<D, E, TSelectData>({
    queryKey: [...queryKey, authToken].filter(Boolean),
    queryFn: async () => {
      const headers = {
        ...(authToken && { Authorization: authToken }),
        ...requestHeaders,
      };

      let silentError = null;

      const response = await client
        .request(query, variables, headers)
        .catch((e) => {
          handleKrakenErrors({
            errorResponse: e,
            onRefreshExpired: expireSession,
            onAccessTokenExpired: clearAccessTokens,
            onAuthHeaderNotProvided: clearAccessTokens,
          });

          if (silentErrors) {
            silentError = e;
            return;
          }
          throw e;
        });

      if (silentError) {
        return silentError;
      }

      return validateFn(response);
    },
    enabled: !!(enabled && hasLoaded), // Ensure explicit boolean
    select,
    ...options, // Keep this last to override default values if needed
  });
}

export function useKrakenInfiniteQuery<D, E>({
  query,
  validateFn,
  requestHeaders,
  silentErrors,
  enabled = true,
  variables,
  queryKey,
  ...options
}: UseKrakenInfiniteQueryArgs<D, E>) {
  const {
    accessToken,
    expireSession,
    clearAccessTokens,
    hasLoaded,
    masqueradeToken,
  } = useAuth();

  /**
   * Token to use as Authorization value.
   */
  const authToken = masqueradeToken ?? accessToken;

  return useInfiniteQuery<
    ResponseWithEdges<D>,
    E,
    InfiniteData<ResponseWithEdges<D>>
  >({
    queryKey: [queryKey, authToken].filter(Boolean), // Ensure queryKey is not duplicated
    queryFn: async ({ pageParam = '0' }) => {
      const headers = {
        ...(authToken && { Authorization: authToken }),
        ...requestHeaders,
      };

      let silentError = null;

      const response = await client
        .request(query, { ...variables, cursor: pageParam }, headers)
        .catch((e) => {
          handleKrakenErrors({
            errorResponse: e,
            onRefreshExpired: expireSession,
            onAccessTokenExpired: clearAccessTokens,
            onAuthHeaderNotProvided: clearAccessTokens,
          });

          if (silentErrors) {
            silentError = e;
            return;
          }
          throw e;
        });

      if (silentError) return silentError;

      // Ensure response is properly formatted as ResponseWithEdges<D>
      const validatedData: ResponseWithEdges<D> = await validateFn(response);

      return validatedData;
    },
    enabled: !!(hasLoaded && enabled),
    ...options, // Spread options first to prevent overriding `getNextPageParam`
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo?.hasNextPage ? lastPage.pageInfo.endCursor : undefined,
    select: (data) => ({
      pages: data.pages.map((page) => page),
      pageParams: data.pageParams,
    }),
  });
}

export function useKrakenMutation<D, E, V>({
  mutation,
  validateFn,
  requestHeaders,
  silentErrors,
  ...options
}: UseKrakenMutationArgs<D, E, V>) {
  const { accessToken, masqueradeToken, clearAccessTokens, expireSession } =
    useAuth();

  /**
   * Token to use as Authorization value.
   */
  const authToken = masqueradeToken ?? accessToken;

  return useMutation({
    mutationFn: async (variables: V) => {
      const headers = {
        ...(authToken && { Authorization: authToken }),
        ...requestHeaders,
      };
      const requestVariables = variables ? variables : undefined;

      const operation = mutation(variables);

      let silentError = null;

      const response = await client
        .request(operation, requestVariables, headers)
        .catch((e) => {
          handleKrakenErrors({
            errorResponse: e,
            onRefreshExpired: expireSession,
            onAccessTokenExpired: clearAccessTokens,
            onAuthHeaderNotProvided: clearAccessTokens,
          });

          if (silentErrors) {
            silentError = e;
            return;
          }
          throw e;
        });

      if (silentError) {
        return silentError;
      }

      return validateFn(response);
    },
    retry: (_, errorResponse) => {
      if (!isKrakenErrorResponse(errorResponse)) {
        return false;
      }
      const [firstError] = errorResponse.response.errors;
      const { errorType } = getErrorDescriptionWithType(firstError);

      if (errorType === 'ACCESS_TOKEN_EXPIRED') {
        clearAccessTokens();
        return true;
      }
      return false;
    },
    ...options, // Spread options last to ensure default values remain
  });
}

export default client;
